/**
 * Serviço para buscar dados dinâmicos usados nos templates
 * 
 * Centraliza a busca de informações necessárias para popular
 * campos dinâmicos como setores, cargos e fornecedores.
 */

import { api } from '../../../services/axios'
import { authService } from '../../../services/auth'
import type { Processo } from '../../../models'
import { integracaoDadosService } from '../../../services/integracaodados'

export interface Setor {
  id: number
  nome_setor: string
  equipe: boolean
  setor_fk: number | null
}

export interface Fornecedor {
  nome: string
  razao_social: string
  cnpj: string
}

export interface Cargo {
  nome: string
}

export interface Sistema {
  id: number
  nome: string
  descricao?: string
  modulos?: ModuloSistema[] // Pode já vir na resposta de /sistemas
  perfis?: PerfilSistema[] // Pode já vir na resposta de /sistemas
}

export interface ModuloSistema {
  id: number
  sistema_id: number
  nome: string
  descricao?: string
}

export interface PerfilSistema {
  id: number
  modulo_id?: number // ID do módulo (pode ser opcional se perfil pertence diretamente ao sistema)
  sistema_fk?: number // ID do sistema (algumas APIs podem retornar assim)
  nome: string
  descricao?: string
  active?: boolean
}

/**
 * Busca todos os sistemas cadastrados
 */
export async function getSistemas(): Promise<Array<{ value: string; label: string }>> {
  try {
    await authService.ensureSanctumInitialized()
    const response = await api.get<Sistema[]>('/sistemas')
    
    return response.data
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(sistema => ({
        value: sistema.id.toString(),
        label: sistema.nome,
      }))
  } catch (error) {
    console.error('Erro ao buscar sistemas:', error)
    return []
  }
}

/**
 * Busca detalhes completos de um sistema (incluindo módulos e perfis)
 */
export async function getSistemaCompleto(sistemaId: number): Promise<{
  sistema: Sistema
  modulos: ModuloSistema[]
  perfis: PerfilSistema[]
} | null> {
  try {
    await authService.ensureSanctumInitialized()
    const response = await api.get<any>(`/sistemas/${sistemaId}`)
    
    console.log('[getSistemaCompleto] Resposta do backend:', response.data)
    
    // Verificar se a resposta já contém módulos e perfis no objeto do sistema
    if (response.data.modulos && response.data.perfis) {
      // Formato 1: Sistema com módulos e perfis dentro
      return {
        sistema: response.data,
        modulos: response.data.modulos || [],
        perfis: response.data.perfis || [],
      }
    } else if (response.data.sistema) {
      // Formato 2: Estrutura separada { sistema, modulos, perfis }
      return {
        sistema: response.data.sistema,
        modulos: response.data.modulos || [],
        perfis: response.data.perfis || [],
      }
    } else {
      console.error('[getSistemaCompleto] Formato de resposta desconhecido:', response.data)
      return null
    }
  } catch (error) {
    console.error('Erro ao buscar detalhes do sistema:', error)
    return null
  }
}

/**
 * Busca módulos de um sistema específico
 */
export async function getModulosPorSistema(sistemaId: number): Promise<Array<{ value: string; label: string }>> {
  try {
    const data = await getSistemaCompleto(sistemaId)
    if (!data) return []
    
    return data.modulos
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(modulo => ({
        value: modulo.id.toString(),
        label: modulo.nome,
      }))
  } catch (error) {
    console.error('Erro ao buscar módulos:', error)
    return []
  }
}

/**
 * Busca perfis de um módulo específico
 */
export async function getPerfisPorModulo(moduloId: number): Promise<Array<{ value: string; label: string }>> {
  try {
    await authService.ensureSanctumInitialized()
    const response = await api.get<Sistema[]>('/sistemas')
    
    // Buscar todos os sistemas e encontrar o módulo
    for (const sistema of response.data) {
      const detalhes = await getSistemaCompleto(sistema.id)
      if (!detalhes) continue
      
      const modulo = detalhes.modulos.find(m => m.id === moduloId)
      if (modulo) {
        const perfisDoModulo = detalhes.perfis.filter(p => p.modulo_id === moduloId)
        return perfisDoModulo
          .sort((a, b) => a.nome.localeCompare(b.nome))
          .map(perfil => ({
            value: perfil.id.toString(),
            label: perfil.nome,
          }))
      }
    }
    
    return []
  } catch (error) {
    console.error('Erro ao buscar perfis:', error)
    return []
  }
}

/**
 * Busca todos os setores e equipes do sistema
 */
export async function getSetoresEUnidades(): Promise<Array<{ value: string; label: string }>> {
  try {
    await authService.ensureSanctumInitialized()
    const response = await api.get<Processo[]>('/processos/filter')
    
    // Extrair setores únicos
    const setoresMap = new Map<number, { id: number; nome: string; equipe: boolean }>()
    
    response.data.forEach((processo: Processo) => {
      if (processo.setor) {
        const setor = processo.setor
        if (!setoresMap.has(setor.id)) {
          setoresMap.set(setor.id, {
            id: setor.id,
            nome: setor.nome_setor,
            equipe: setor.equipe ?? false,
          })
        }
      }
    })
    
    // Converter para formato de opções
    return Array.from(setoresMap.values())
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(setor => ({
        value: setor.id.toString(),
        label: setor.equipe ? `${setor.nome} (Equipe)` : setor.nome,
      }))
  } catch (error) {
    console.error('Erro ao buscar setores:', error)
    return []
  }
}

/**
 * Busca lista de cargos
 */
export async function getCargos(): Promise<Array<{ value: string; label: string }>> {
  try {
    const cargos = await integracaoDadosService.getCargos()
    return cargos.map(cargo => ({
      value: cargo.nome,
      label: cargo.nome,
    }))
  } catch (error) {
    console.error('Erro ao buscar cargos:', error)
    return []
  }
}

/**
 * Busca lista de fornecedores
 */
export async function getFornecedores(): Promise<Array<{ value: string; label: string; data: Fornecedor }>> {
  try {
    const fornecedores = await integracaoDadosService.getFornecedores()
    return fornecedores.map(fornecedor => ({
      value: fornecedor.cnpj,
      label: `${fornecedor.nome} - ${formatarCNPJ(fornecedor.cnpj)}`,
      data: {
        nome: fornecedor.nome,
        razao_social: fornecedor.razao_social,
        cnpj: fornecedor.cnpj,
      },
    }))
  } catch (error) {
    console.error('Erro ao buscar fornecedores:', error)
    return []
  }
}

/**
 * Adiciona um novo fornecedor
 */
export async function adicionarFornecedor(fornecedor: Fornecedor): Promise<boolean> {
  try {
    await integracaoDadosService.adicionarFornecedor(fornecedor)
    return true
  } catch (error) {
    console.error('Erro ao adicionar fornecedor:', error)
    throw error
  }
}

/**
 * Valida CNPJ
 */
export function validarCNPJ(cnpj: string): boolean {
  // Remove caracteres não numéricos
  const cnpjNumerico = cnpj.replace(/[^\d]/g, '')
  
  // Verifica se tem 14 dígitos
  if (cnpjNumerico.length !== 14) return false
  
  // Verifica se não é sequência de números iguais
  if (/^(\d)\1+$/.test(cnpjNumerico)) return false
  
  // Validação dos dígitos verificadores
  let tamanho = cnpjNumerico.length - 2
  let numeros = cnpjNumerico.substring(0, tamanho)
  const digitos = cnpjNumerico.substring(tamanho)
  let soma = 0
  let pos = tamanho - 7
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--
    if (pos < 2) pos = 9
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado !== parseInt(digitos.charAt(0))) return false
  
  tamanho = tamanho + 1
  numeros = cnpjNumerico.substring(0, tamanho)
  soma = 0
  pos = tamanho - 7
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--
    if (pos < 2) pos = 9
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado !== parseInt(digitos.charAt(1))) return false
  
  return true
}

/**
 * Valida CPF
 */
export function validarCPF(cpf: string): boolean {
  // Remove caracteres não numéricos
  const cpfNumerico = cpf.replace(/[^\d]/g, '')
  
  // Verifica se tem 11 dígitos
  if (cpfNumerico.length !== 11) return false
  
  // Verifica se não é sequência de números iguais
  if (/^(\d)\1+$/.test(cpfNumerico)) return false
  
  // Validação dos dígitos verificadores
  let soma = 0
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpfNumerico.charAt(i)) * (10 - i)
  }
  
  let resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) resto = 0
  if (resto !== parseInt(cpfNumerico.charAt(9))) return false
  
  soma = 0
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpfNumerico.charAt(i)) * (11 - i)
  }
  
  resto = (soma * 10) % 11
  if (resto === 10 || resto === 11) resto = 0
  if (resto !== parseInt(cpfNumerico.charAt(10))) return false
  
  return true
}

/**
 * Formata CPF para exibição
 */
export function formatarCPF(cpf: string): string {
  const numeros = cpf.replace(/[^\d]/g, '')
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

/**
 * Formata CNPJ para exibição
 */
export function formatarCNPJ(cnpj: string): string {
  const numeros = cnpj.replace(/[^\d]/g, '')
  return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}
